import { useState } from 'react';
// @mui
import {
  Avatar,
  Checkbox,
  Stack,
  TableRow,
  TableCell,
  Typography,
  MenuItem,
} from '@mui/material';
// components
import { useNavigate } from 'react-router-dom';
import Label from 'src/components/Label';
import Iconify from 'src/components/Iconify';
import { TableMoreMenu } from 'src/components/table';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { Diploma } from 'src/@types/Diploma';
import type { LandingSummaryRow } from './landing/useLandingSummaries';
import { siteUrl } from 'src/utils/siteUrl';
import LandingSummaryCell from './landing/components/LandingSummaryCell';

// ----------------------------------------------------------------------

type Props = {
  row: Diploma;
  selected: boolean;
  canWrite?: boolean;
  visibleColumns?: string[];
  // Enrolled count per batch, keyed `${diplomaId}|${batchName}`.
  countMap?: Record<string, number>;
  // Landing page summary for this diploma (Landing column); undefined = none yet.
  landingSummary?: LandingSummaryRow;
  landingLoaded?: boolean;
  onEditRow: VoidFunction;
  onSelectRow: VoidFunction;
  onDeleteRow: VoidFunction;
};

export default function DiplomaTableRow({
  row,
  selected,
  canWrite = true,
  visibleColumns,
  countMap,
  landingSummary,
  landingLoaded,
  onEditRow,
  onSelectRow,
  onDeleteRow,
}: Props) {
  const { _id, title, image, description, active, batches, slug } = row;
  const navigate = useNavigate();
  const reportPath = `${PATH_DASHBOARD.diplomas}/${_id}/report`;
  const price = (row as any).price;
  const priceUsd = (row as any).price_usd;
  const durationWeeks = (row as any).durationWeeks;
  const showCol = (id: string) => !visibleColumns || visibleColumns.includes(id);

  const batchList = batches || [];
  const activeBatches = batchList.filter((b) => b.active !== false).length;
  const enrolledOf = (batchName: string) => (countMap || {})[`${_id}|${batchName}`] || 0;
  const totalEnrolled = batchList.reduce((sum, b) => sum + enrolledOf(b.name), 0);

  const [openMenu, setOpenMenuActions] = useState<HTMLElement | null>(null);
  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) =>
    setOpenMenuActions(event.currentTarget);
  const handleCloseMenu = () => setOpenMenuActions(null);

  return (
    <TableRow hover selected={selected}>
      <TableCell padding="checkbox">
        <Checkbox checked={selected} onClick={onSelectRow} />
      </TableCell>

      {showCol('title') && (
        <TableCell>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Avatar
              alt={title}
              src={image || ''}
              variant="rounded"
              sx={{ width: 40, height: 40 }}
            >
              {title?.charAt(0)?.toUpperCase()}
            </Avatar>
            <Typography
              variant="subtitle2"
              onClick={() => navigate(reportPath)}
              sx={{
                maxWidth: 220,
                cursor: 'pointer',
                '&:hover': { color: 'primary.main', textDecoration: 'underline' },
              }}
            >
              {title}
            </Typography>
          </Stack>
        </TableCell>
      )}

      {showCol('description') && (
        <TableCell align="left">
          <Typography
            variant="body2"
            color="text.secondary"
            title={description || undefined}
            sx={{
              maxWidth: 280,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {description || '—'}
          </Typography>
        </TableCell>
      )}

      {showCol('price') && (
        <TableCell align="left">
          {price ? (
            <Stack spacing={0}>
              <Typography variant="body2">{Number(price).toLocaleString()} EGP</Typography>
              {priceUsd ? (
                <Typography variant="caption" color="text.secondary">
                  ${Number(priceUsd).toLocaleString()}
                </Typography>
              ) : null}
            </Stack>
          ) : (
            '—'
          )}
        </TableCell>
      )}

      {showCol('duration') && (
        <TableCell align="center">
          {durationWeeks ? `${durationWeeks} wks` : '—'}
        </TableCell>
      )}

      {showCol('batches') && (
        <TableCell align="left">
          {batchList.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No batches
            </Typography>
          ) : (
            <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
              {batchList.slice(0, 3).map((b, i) => {
                const enrolled = enrolledOf(b.name);
                const cap = b.capacity ? `/${b.capacity}` : '';
                const full = !!b.capacity && enrolled >= (b.capacity as number);
                return (
                  <Label
                    key={b._id || i}
                    color={
                      b.active === false ? 'default' : full ? 'warning' : 'info'
                    }
                  >
                    {b.name} · {enrolled}
                    {cap}
                  </Label>
                );
              })}
              {batchList.length > 3 && (
                <Label color="default">+{batchList.length - 3}</Label>
              )}
            </Stack>
          )}
        </TableCell>
      )}

      {showCol('batchCount') && (
        <TableCell align="center">
          {batchList.length} {batchList.length ? `(${activeBatches} active)` : ''}
          {totalEnrolled ? ` · ${totalEnrolled} enrolled` : ''}
        </TableCell>
      )}

      {showCol('landing') && (
        <TableCell align="left">
          <LandingSummaryCell
            summary={landingSummary}
            loaded={landingLoaded}
            to={PATH_DASHBOARD.diplomaLanding(_id as string)}
          />
        </TableCell>
      )}

      {showCol('status') && (
        <TableCell align="center">
          <Label color={active ? 'success' : 'default'}>
            {active ? 'Active' : 'Inactive'}
          </Label>
        </TableCell>
      )}

      <TableCell align="right">
        <TableMoreMenu
          open={openMenu}
          onOpen={handleOpenMenu}
          onClose={handleCloseMenu}
          actions={
            <>
              <MenuItem
                onClick={() => {
                  navigate(reportPath);
                  handleCloseMenu();
                }}
              >
                <Iconify icon={'eva:bar-chart-2-outline'} />
                View report
              </MenuItem>
              <MenuItem
                onClick={() => {
                  navigate(PATH_DASHBOARD.diplomaLanding(_id as string));
                  handleCloseMenu();
                }}
              >
                <Iconify icon={'eva:browser-outline'} />
                Landing page
              </MenuItem>
              {slug && active !== false && (
                <MenuItem
                  component="a"
                  href={siteUrl(slug, 'ar')}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleCloseMenu}
                >
                  <Iconify icon={'eva:external-link-outline'} />
                  Open page (AR)
                </MenuItem>
              )}
              {slug && active !== false && (
                <MenuItem
                  component="a"
                  href={siteUrl(slug, 'en')}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleCloseMenu}
                >
                  <Iconify icon={'eva:external-link-outline'} />
                  Open page (EN)
                </MenuItem>
              )}
              {canWrite && (
                <MenuItem
                  onClick={() => {
                    onEditRow();
                    handleCloseMenu();
                  }}
                >
                  <Iconify icon={'eva:edit-fill'} />
                  Edit
                </MenuItem>
              )}
              {canWrite && (
                <MenuItem
                  onClick={() => {
                    onDeleteRow();
                    handleCloseMenu();
                  }}
                  sx={{ color: 'error.main' }}
                >
                  <Iconify icon={'eva:trash-2-outline'} />
                  Delete
                </MenuItem>
              )}
            </>
          }
        />
      </TableCell>
    </TableRow>
  );
}
